import React, { useEffect, useState, useRef } from 'react';
import { accountAPI } from '../services/api';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

const API_BASE = process.env.REACT_APP_API_URL || 'https://high.innohed.com/api';
const WS_BASE = API_BASE.replace('/api', '');

// 숫자 변경 시 깜빡이는 컴포넌트
const FlashValue: React.FC<{ value: number | undefined; format: (v: number) => string; color?: string }> = ({ value, format, color }) => {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prevRef.current !== undefined && prevRef.current !== value) {
      setFlash(true);
      setTimeout(() => setFlash(false), 600);
    }
    prevRef.current = value;
  }, [value]);

  if (value == null) return <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span>;

  return (
    <span style={{
      color: flash ? '#ffe066' : (color || '#fff'),
      fontWeight: 600,
      transition: 'color 0.1s',
    }}>
      {format(value)}
    </span>
  );
};

const StatCard: React.FC<{ title: string; value: string | number; sub?: string; color?: string; icon: string }> = ({
  title, value, sub, color = '#667eea', icon
}) => (
  <div style={{ ...styles.statCard, borderColor: `${color}30` }}>
    <div style={{ ...styles.statIcon, background: `${color}20`, color }}>{icon}</div>
    <div style={styles.statValue}>{value}</div>
    <div style={styles.statTitle}>{title}</div>
    {sub && <div style={styles.statSub}>{sub}</div>}
  </div>
);

const DashboardPage: React.FC = () => {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [aBookAccounts, setABookAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [equityMap, setEquityMap] = useState<Record<string, any>>({});
  const [nameMap, setNameMap] = useState<Record<string, string>>({});
  const stompClientRef = useRef<Client | null>(null);

  const fetchData = async () => {
    try {
      const [allRes, aBookRes] = await Promise.all([
        accountAPI.getAll(),
        accountAPI.getABook(),
      ]);
      setAccounts(allRes.data);
      setABookAccounts(aBookRes.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_BASE}/api/ws`),
      reconnectDelay: 5000,
      onConnect: () => {
        console.log('[WS] STOMP 연결됨');
        client.subscribe('/topic/equity-updates', (msg) => {
          const data = JSON.parse(msg.body);
          setEquityMap(prev => ({
            ...prev,
            [data.accountId]: {
              equity: data.equity,
              balance: data.balance,
              drawdown: data.drawdown,
              name: data.name,
              timestamp: data.timestamp,
            }
          }));
          // 이름 저장
          if (data.name) {
            setNameMap(prev => ({ ...prev, [data.accountId]: data.name }));
          }
        });
        client.subscribe('/topic/a-book-events', () => fetchData());
        client.subscribe('/topic/b-book-events', () => fetchData());
      },
    });
    client.activate();
    stompClientRef.current = client;
    return () => { client.deactivate(); };
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const bBookAccounts = accounts.filter(a => a.isBBook);
  const bBookCount = bBookAccounts.length;
  const aBookCount = aBookAccounts.length;
  const totalCount = accounts.length;

  const getRt = (accountId: string) => equityMap[accountId];
  const getName = (accountId: string) => nameMap[accountId] || '';

  const formatNum = (v: number) =>
    v.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const ddColor = (dd?: number) => {
    if (dd == null) return 'rgba(255,255,255,0.3)';
    if (dd < -10) return '#ff4d4f';
    if (dd < -5) return '#ff7a45';
    if (dd < 0) return '#ffa940';
    return '#52c41a';
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
      `}</style>

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>대시보드</h1>
          <p style={styles.subtitle}>
            마지막 업데이트: {lastUpdated.toLocaleTimeString('ko-KR')}
            <span style={{ color: '#52c41a', fontSize: '11px', fontWeight: 600, marginLeft: '8px', animation: 'pulse 2s infinite' }}>● LIVE</span>
          </p>
        </div>
        <button style={styles.refreshBtn} onClick={fetchData}>🔄 새로고침</button>
      </div>

      <div style={styles.statsGrid}>
        <StatCard icon="👥" title="전체 계좌" value={totalCount} sub="등록된 계좌 수" color="#667eea" />
        <StatCard icon="🟢" title="B북 운영" value={bBookCount} sub="정상 운영 중" color="#00d2ff" />
        <StatCard icon="🔴" title="A북 전환" value={aBookCount} sub="현재 헤지 중" color="#ff6b6b" />
        <StatCard icon="📊" title="전환율" value={totalCount > 0 ? `${((aBookCount / totalCount) * 100).toFixed(1)}%` : '0%'} sub="A북 비율" color="#f7971e" />
      </div>

      {/* A북 전환 계좌 */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>🔴 A북 전환 계좌 ({aBookCount})</h2>
        {loading ? <div style={styles.loading}>로딩 중...</div>
        : aBookAccounts.length === 0 ? <div style={styles.empty}>현재 A북 전환된 계좌가 없습니다.</div>
        : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>계좌번호</th>
                  <th style={styles.th}>이름</th>
                  <th style={styles.th}>Balance</th>
                  <th style={styles.th}>Equity</th>
                  <th style={styles.th}>Drawdown</th>
                  <th style={styles.th}>전환 시각</th>
                  <th style={styles.th}>액션</th>
                </tr>
              </thead>
              <tbody>
                {aBookAccounts.map((state: any) => {
                  const rt = getRt(state.accountId);
                  const isRt = !!rt;
                  return (
                    <tr key={state.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.accountId}>{state.accountId}</span>
                        {isRt && <span style={styles.rtBadge}>RT</span>}
                      </td>
                      <td style={styles.td}>
                        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                          {getName(state.accountId) || '-'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <FlashValue value={rt?.balance ?? state.triggerBalance} format={formatNum} color="rgba(255,255,255,0.8)" />
                      </td>
                      <td style={styles.td}>
                        <FlashValue value={rt?.equity ?? state.lastEquity} format={formatNum} color="rgba(255,255,255,0.8)" />
                      </td>
                      <td style={styles.td}>
                        <FlashValue
                          value={rt?.drawdown ?? state.lastDrawdown}
                          format={v => `${v.toFixed(2)}%`}
                          color={ddColor(rt?.drawdown ?? state.lastDrawdown)}
                        />
                      </td>
                      <td style={styles.td}>
                        {state.triggeredAt ? new Date(state.triggeredAt).toLocaleString('ko-KR') : '-'}
                      </td>
                      <td style={styles.td}>
                        <ReturnButton accountId={state.accountId} onSuccess={fetchData} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* B북 운영 계좌 */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>🟢 B북 운영 계좌 ({bBookCount})</h2>
        {loading ? <div style={styles.loading}>로딩 중...</div>
        : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>계좌번호</th>
                  <th style={styles.th}>이름</th>
                  <th style={styles.th}>Balance</th>
                  <th style={styles.th}>Equity</th>
                  <th style={styles.th}>Drawdown</th>
                  <th style={styles.th}>임계값</th>
                  <th style={styles.th}>등급</th>
                </tr>
              </thead>
              <tbody>
                {bBookAccounts.map((acc: any) => {
                  const rt = getRt(acc.accountId);
                  const isRt = !!rt;
                  return (
                    <tr key={acc.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.accountId}>{acc.accountId}</span>
                        {isRt && <span style={styles.rtBadge}>RT</span>}
                      </td>
                      <td style={styles.td}>
                        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                          {getName(acc.accountId) || '-'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <FlashValue value={rt?.balance} format={formatNum} color="rgba(255,255,255,0.8)" />
                      </td>
                      <td style={styles.td}>
                        <FlashValue value={rt?.equity} format={formatNum} color="rgba(255,255,255,0.8)" />
                      </td>
                      <td style={styles.td}>
                        <FlashValue
                          value={rt?.drawdown}
                          format={v => `${v.toFixed(2)}%`}
                          color={ddColor(rt?.drawdown)}
                        />
                      </td>
                      <td style={styles.td}>{acc.thresholdEntry}%</td>
                      <td style={styles.td}><GradeBadge grade={acc.grade} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {bBookAccounts.length === 0 && <div style={styles.empty}>B북 운영 계좌가 없습니다.</div>}
          </div>
        )}
      </div>
    </div>
  );
};

const ReturnButton: React.FC<{ accountId: string; onSuccess: () => void }> = ({ accountId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const handleReturn = async () => {
    if (!window.confirm(`계좌 ${accountId}를 B북으로 복귀시키겠습니까?`)) return;
    setLoading(true);
    try { await accountAPI.returnToBBook(accountId); onSuccess(); }
    catch { alert('복귀 처리 중 오류가 발생했습니다.'); }
    finally { setLoading(false); }
  };
  return (
    <button style={styles.returnBtn} onClick={handleReturn} disabled={loading}>
      {loading ? '처리 중...' : 'B북 복귀'}
    </button>
  );
};

const GradeBadge: React.FC<{ grade: string }> = ({ grade }) => {
  const colors: Record<string, string> = { S: '#f7971e', A: '#667eea', B: '#00d2ff', C: '#aaa' };
  return (
    <span style={{ padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: `${colors[grade] || '#aaa'}20`, color: colors[grade] || '#aaa' }}>
      {grade}
    </span>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { color: '#fff', maxWidth: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' },
  title: { fontSize: '28px', fontWeight: 700, color: '#fff', margin: '0 0 4px' },
  subtitle: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 },
  refreshBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '8px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '13px', cursor: 'pointer' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' },
  statCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid', borderRadius: '16px', padding: '24px', textAlign: 'center' },
  statIcon: { width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 12px' },
  statValue: { fontSize: '32px', fontWeight: 700, color: '#fff', marginBottom: '4px' },
  statTitle: { color: 'rgba(255,255,255,0.6)', fontSize: '13px', fontWeight: 500 },
  statSub: { color: 'rgba(255,255,255,0.3)', fontSize: '11px', marginTop: '4px' },
  section: { marginBottom: '32px' },
  sectionTitle: { fontSize: '18px', fontWeight: 600, color: '#fff', marginBottom: '16px' },
  tableWrapper: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.05)' },
  td: { padding: '12px 16px', color: 'rgba(255,255,255,0.8)', fontSize: '14px' },
  accountId: { fontFamily: 'monospace', background: 'rgba(102,126,234,0.15)', padding: '2px 8px', borderRadius: '4px', color: '#667eea' },
  rtBadge: { marginLeft: '6px', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, background: 'rgba(82,196,26,0.2)', color: '#52c41a' },
  returnBtn: { background: 'rgba(0,210,255,0.15)', border: '1px solid rgba(0,210,255,0.3)', borderRadius: '6px', padding: '4px 12px', color: '#00d2ff', fontSize: '12px', cursor: 'pointer' },
  loading: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
  empty: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
};

export default DashboardPage;
